<template>
  <div class="sync-page mx-4 mt-3">
    <v-btn
      flat
      class="pl-0"
      :to="{ name: 'SelectionDetail', params: { id: $route.params.id } }"
    >
      <v-icon left>arrow_back</v-icon>
      Selection details
    </v-btn>

    <v-card class="sync-card elevation-0 mt-2">
      <v-card-title class="sync-header px-0 pt-0 pb-4">
        <div>
          <div class="section-label">NetX sync</div>
          <div class="display-1 mt-1">{{ listName }}</div>
          <div class="sync-meta mt-2">
            <span v-if="run">Run #{{ run.id }}</span>
            <span v-if="selectionTotal !== null">{{ selectionTotal }} artworks</span>
            <span v-if="run">Started {{ formatDateTime(run.created_at) }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          v-if="canManageNetx && (!run || !active)"
          color="black"
          dark
          :loading="loading"
          @click="start"
        >
          <v-icon left>sync</v-icon>
          {{ run ? 'Refresh NetX metadata' : 'Sync NetX metadata' }}
        </v-btn>
        <v-btn v-else-if="run" flat :loading="loading" @click="refresh">
          <v-icon left>refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-alert v-if="error" :value="true" color="error" outline class="mt-3">
        {{ error }}
      </v-alert>

      <v-alert v-if="mediaMessage" :value="true" color="success" outline class="mt-3">
        {{ mediaMessage }}
      </v-alert>

      <v-progress-linear v-if="loading && !run" indeterminate color="black"></v-progress-linear>

      <v-card-text v-if="run" class="px-0 pb-0">
        <div class="progress-heading">
          <div>
            <span class="status-pill" :class="runStatusClass">{{ runStatusLabel }}</span>
            <span class="progress-copy ml-3">
              <strong>{{ run.progress.completed }}</strong> of {{ run.progress.total }} finished
            </span>
          </div>
          <span v-if="active" class="live-indicator">Updating automatically</span>
          <span v-else class="grey--text caption">Finished {{ formatDateTime(run.finished_at) }}</span>
        </div>

        <v-progress-linear
          :value="run.progress.percent"
          :indeterminate="active && run.progress.total === 0"
          color="black"
          background-color="grey lighten-3"
          height="6"
          class="mt-3"
        ></v-progress-linear>

        <div class="sync-stats">
          <div class="sync-stat">
            <span class="sync-stat-value">{{ run.progress.completed }}</span>
            <span class="sync-stat-label">Finished</span>
          </div>
          <div class="sync-stat">
            <span class="sync-stat-value">{{ countFor('succeeded') }}</span>
            <span class="sync-stat-label">Synced</span>
          </div>
          <div class="sync-stat">
            <span class="sync-stat-value">{{ countFor('partial') }}</span>
            <span class="sync-stat-label">Partial</span>
          </div>
          <div class="sync-stat">
            <span class="sync-stat-value">{{ countFor('no_match') }}</span>
            <span class="sync-stat-label">No match</span>
          </div>
          <div class="sync-stat" :class="{ 'has-errors': countFor('failed') }">
            <span class="sync-stat-value">{{ countFor('failed') }}</span>
            <span class="sync-stat-label">Failed</span>
          </div>
        </div>

        <div v-if="canManageNetx && !active" class="run-actions">
          <div class="run-action-copy">
            <span class="grey--text caption">Run actions</span>
            <span v-if="retryableCount" class="grey--text caption ml-3">
              {{ retryableCount }} artwork{{ retryableCount === 1 ? '' : 's' }} can be retried.
            </span>
          </div>
          <div class="run-action-buttons">
            <v-btn
              small
              outline
              :loading="generatingWebsiteMedia"
              @click="generateWebsiteMedia"
            >
              <v-icon left small>collections</v-icon>
              Generate Website media
            </v-btn>
            <v-btn
              v-if="retryableCount"
              small
              color="error"
              outline
              :loading="retrying"
              @click="retryFailed"
            >
              Retry failed / partial
            </v-btn>
          </div>
        </div>

        <div class="section-heading mt-4">
          <div>
            <div class="title">Artwork results</div>
            <div class="caption grey--text">Individual sync status and errors</div>
          </div>
        </div>

        <v-data-table
          class="sync-table elevation-0"
          :headers="headers"
          :items="run.items"
          :pagination.sync="pagination"
          rows-per-page-text="Artworks per page"
          item-key="id"
        >
          <template slot="items" slot-scope="props">
            <tr>
              <td>
                <router-link
                  v-if="props.item.artwork_id"
                  class="artwork-link"
                  :to="{ name: 'EditArtwork', params: { id: props.item.artwork_id } }"
                >
                  {{ artworkTitle(props.item) }}
                </router-link>
                <span v-else>{{ artworkTitle(props.item) }}</span>
              </td>
              <td>{{ props.item.accession_number || '—' }}</td>
              <td>
                <span class="status-pill" :class="itemStatusClass(props.item.status)">
                  {{ statusLabel(props.item.status) }}
                </span>
              </td>
              <td>{{ itemSummary(props.item) }}</td>
              <td :class="{ 'error--text': props.item.error_message }">
                {{ props.item.error_message || '—' }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-text v-else-if="!loading" class="empty-state px-0 grey--text">
        <template v-if="canManageNetx">
          Start a metadata sync to refresh the cached NetX assets in this selection. After it
          finishes, use the run actions to generate Website artwork photos and public images.
        </template>
        <template v-else>
          No NetX sync runs have been started for this selection.
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { canSyncNetx } from '@/utils/netxArtworkReview'
import {
  startNetxListSync,
  loadNetxListSyncRun,
  loadLatestNetxListSyncRun,
  retryNetxListSyncRun,
  generateNetxWebsiteMedia,
  listSyncError
} from '@/utils/netxListSync'

export default {
  data () {
    return {
      run: null,
      loading: false,
      retrying: false,
      generatingWebsiteMedia: false,
      error: '',
      mediaMessage: '',
      timer: null,
      pagination: {
        rowsPerPage: 25,
        sortBy: 'id'
      },
      headers: [
        { text: 'Artwork', value: 'artwork_title' },
        { text: 'Accession', value: 'accession_number' },
        { text: 'Status', value: 'status' },
        { text: 'Assets', value: 'assets_seen' },
        { text: 'Error', value: 'error_message' }
      ]
    }
  },
  computed: {
    list () {
      return this.$store.state.lists.find(list => String(list.id) === String(this.$route.params.id))
    },
    listName () {
      return (this.run && this.run.list && this.run.list.name) ||
        (this.list && this.list.name) ||
        `Selection #${this.$route.params.id}`
    },
    active () {
      return this.run && ['queued', 'running'].includes(this.run.status)
    },
    retryableCount () {
      if (!this.run) return 0
      return this.countFor('failed') + this.countFor('partial')
    },
    issueCount () {
      if (!this.run) return 0
      return this.retryableCount + this.countFor('no_match')
    },
    canManageNetx () {
      return canSyncNetx(this.c_user)
    },
    selectionTotal () {
      if (this.run) return this.run.progress.total
      if (!this.list || !Array.isArray(this.list.artworks)) return null
      return this.list.artworks.length
    },
    runStatusLabel () {
      if (!this.run) return ''
      if (this.run.status === 'completed' && this.issueCount) return 'Completed with issues'
      return this.humanizeStatus(this.run.status)
    },
    runStatusClass () {
      if (!this.run) return 'status-neutral'
      if (this.run.status === 'failed') return 'status-error'
      if (this.run.status === 'completed' && this.issueCount) return 'status-warning'
      if (this.run.status === 'completed') return 'status-success'
      if (this.active) return 'status-active'
      return 'status-neutral'
    }
  },
  created () {
    this.$store.dispatch('loadLists')
    if (this.$route.query.run_id) this.loadRun(this.$route.query.run_id)
    else this.loadLatestRun()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    async start () {
      if (!this.canManageNetx) return
      this.loading = true
      this.error = ''
      this.mediaMessage = ''
      try {
        const response = await startNetxListSync(this.$route.params.id)
        this.setRun(response.data)
        this.$router.replace({ query: { run_id: this.run.id } })
      } catch (error) {
        this.error = listSyncError(error)
      } finally {
        this.loading = false
      }
    },
    async loadRun (runId) {
      if (this.loading) return
      this.loading = true
      this.error = ''
      try {
        const response = await loadNetxListSyncRun(runId)
        this.setRun(response.data)
      } catch (error) {
        this.error = listSyncError(error)
      } finally {
        this.loading = false
      }
    },
    async loadLatestRun () {
      this.loading = true
      this.error = ''
      try {
        const response = await loadLatestNetxListSyncRun(this.$route.params.id)
        if (response.data.length) this.setRun(await this.loadFullRun(response.data[0].id))
      } catch (error) {
        this.error = listSyncError(error)
      } finally {
        this.loading = false
      }
    },
    async loadFullRun (runId) {
      const response = await loadNetxListSyncRun(runId)
      return response.data
    },
    async refresh () {
      if (this.run) await this.loadRun(this.run.id)
    },
    async retryFailed () {
      if (!this.canManageNetx) return
      this.retrying = true
      this.error = ''
      try {
        const response = await retryNetxListSyncRun(this.run.id)
        this.setRun(response.data)
      } catch (error) {
        this.error = listSyncError(error)
      } finally {
        this.retrying = false
      }
    },
    async generateWebsiteMedia () {
      if (!this.canManageNetx || !this.run || this.active) return
      this.generatingWebsiteMedia = true
      this.error = ''
      this.mediaMessage = ''
      try {
        const response = await generateNetxWebsiteMedia(this.run.id)
        const result = response.data || {}
        const queued = Number(result.queued || 0)
        const skipped = Number(result.skipped || 0)
        const failed = Number(result.failed || 0)
        this.mediaMessage = `${queued} Website asset${queued === 1 ? '' : 's'} queued for media generation` +
          (skipped ? `; ${skipped} skipped` : '') +
          (failed ? `; ${failed} failed to queue` : '') + '.'
      } catch (error) {
        this.error = listSyncError(error)
      } finally {
        this.generatingWebsiteMedia = false
      }
    },
    setRun (run) {
      this.run = run
      if (this.active) this.startPolling()
      else this.stopPolling()
    },
    startPolling () {
      this.stopPolling()
      this.timer = setInterval(() => this.loadRun(this.run.id), 3000)
    },
    stopPolling () {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
    },
    countFor (status) {
      return (this.run && this.run.progress.counts[status]) || 0
    },
    formatDateTime (value) {
      return value ? this.format_date(value, 'YYYY-MM-DD HH:mm') : '—'
    },
    humanizeStatus (status) {
      const label = String(status || '').replace(/_/g, ' ')
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    statusLabel (status) {
      if (status === 'no_match') return 'No accession number match in Netx'
      return this.humanizeStatus(status)
    },
    itemStatusClass (status) {
      if (status === 'succeeded') return 'status-success'
      if (status === 'partial' || status === 'no_match') return 'status-warning'
      if (status === 'failed') return 'status-error'
      if (status === 'processing') return 'status-active'
      return 'status-neutral'
    },
    artworkTitle (item) {
      if (item.artwork_title) return item.artwork_title
      return item.artwork_id ? `Artwork #${item.artwork_id}` : 'Unknown artwork'
    },
    itemSummary (item) {
      if (!item.assets_seen) return '—'
      return `${item.assets_created} new · ${item.assets_updated} updated`
    }
  }
}
</script>

<style scoped>
.sync-page {
  max-width: 1440px;
}

.sync-card {
  padding: 24px 20px 8px;
}

.sync-header {
  border-bottom: 1px solid #d5d5d5;
}

.section-label {
  color: #777;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.sync-meta {
  color: #666;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 8px 20px;
}

.progress-heading,
.run-actions,
.section-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.progress-copy {
  color: #555;
  font-size: 13px;
}

.live-indicator {
  color: #555;
  font-size: 12px;
}

.live-indicator::before {
  animation: pulse 1.6s infinite;
  background: #111;
  border-radius: 50%;
  content: '';
  display: inline-block;
  height: 6px;
  margin-right: 7px;
  width: 6px;
}

.sync-stats {
  border-bottom: 1px solid #d5d5d5;
  border-top: 1px solid #d5d5d5;
  display: grid;
  grid-template-columns: repeat(5, minmax(90px, 1fr));
  margin-top: 24px;
}

.sync-stat {
  border-right: 1px solid #e0e0e0;
  padding: 15px 16px;
}

.sync-stat:first-child {
  padding-left: 0;
}

.sync-stat:last-child {
  border-right: 0;
}

.sync-stat-value,
.sync-stat-label {
  display: block;
}

.sync-stat-value {
  font-size: 22px;
  line-height: 1.15;
}

.sync-stat-label {
  color: #777;
  font-size: 11px;
  letter-spacing: .06em;
  margin-top: 4px;
  text-transform: uppercase;
}

.sync-stat.has-errors .sync-stat-value {
  color: #a83232;
}

.run-actions {
  border-bottom: 1px solid #e0e0e0;
  min-height: 62px;
}

.run-action-copy,
.run-action-buttons {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
}

.run-action-buttons {
  gap: 8px;
}

.section-heading {
  border-bottom: 1px solid #d5d5d5;
  min-height: 58px;
}

.sync-table {
  border-top: 0;
}

.artwork-link {
  color: #111;
  font-weight: 500;
  text-decoration: none;
}

.artwork-link:hover {
  text-decoration: underline;
}

.status-pill {
  border: 1px solid #bbb;
  border-radius: 2px;
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .025em;
  line-height: 22px;
  max-width: 190px;
  padding: 0 8px;
  text-transform: uppercase;
}

.status-success {
  border-color: #4f7a58;
  color: #315a3a;
}

.status-warning {
  border-color: #b28a42;
  color: #80601e;
}

.status-error {
  border-color: #b34b4b;
  color: #9a3030;
}

.status-active {
  background: #111;
  border-color: #111;
  color: #fff;
}

.status-neutral {
  color: #666;
}

.empty-state {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 24px;
  padding-top: 24px;
}

@keyframes pulse {
  0%, 100% { opacity: .25; }
  50% { opacity: 1; }
}

@media (max-width: 720px) {
  .sync-card {
    padding-left: 8px;
    padding-right: 8px;
  }

  .sync-header,
  .progress-heading {
    align-items: flex-start;
  }

  .sync-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .sync-stat {
    border-bottom: 1px solid #e0e0e0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/selections/NetxSync.vue